import React, { useState, useMemo } from 'react';
import Pagination from './Pagination.js';
import '../../styles/Reportes.css';

/**
 * Componente reutilizable para mostrar un historial de transacciones con filtros y paginación.
 * @param {Array} transactions - El array completo de transacciones.
 * @param {boolean} showTabs - Si es `true`, muestra las pestañas de filtro (Todos, ROI, etc.).
 * @param {string} defaultTab - La pestaña que debe estar activa por defecto.
 */
const TransactionHistory = ({ transactions, showTabs = true, defaultTab = 'Todos' }) => {
    const [activeTab, setActiveTab] = useState(defaultTab);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);

    const filteredTransactions = useMemo(() => {
        // Si no se muestran las pestañas, el filtro se basa únicamente en el `defaultTab`.
        const tabToFilter = showTabs ? activeTab : defaultTab;
        switch (tabToFilter) {
            // --- CORRECCIÓN: Añadimos una guarda para asegurar que `transactions` no sea undefined ---
            // Si `transactions` es nulo o indefinido, usamos un array vacío para evitar el error .filter().
            case 'ROI': return (transactions || []).filter(tx => ['ganancia_roi', 'reversion_ganancia'].includes(tx.type));
            case 'Comisiones': return (transactions || []).filter(tx => ['commission', 'commission_bonus', 'commission_manual_fix', 'commission_level_bonus'].includes(tx.type));
            case 'Deducciones': return (transactions || []).filter(tx => tx.amount < 0);
            default: return (transactions || []);
        }
    }, [activeTab, defaultTab, showTabs, transactions]);

    const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
    const paginatedTxs = filteredTransactions.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    const handleTabClick = (tab) => {
        setActiveTab(tab);
        setCurrentPage(1);
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };

    const typeLabels = {
        'ganancia_roi': 'Participación asignada', 'commission': 'Comisión', 'fee': 'Cuota',
        'retiro_aprobado': 'Retiro', 'fee_acelerador': 'Fee Acelerador',
        'fee_reactivacion_acelerador': 'Fee Reactivación', 'fee_acelerador_manual': 'Fee Acelerador (M)',
        'inversion_manual': 'Participación (M)', 'reembolso_retiro': 'Reembolso',
        'pago_acuerdo': 'Pago Acuerdo', 'reversion_ganancia': 'Reversión',
        'ajuste_correccion_negativo': 'Ajuste Saldo', 'ajuste_manual': 'Ajuste Manual',
        'commission_manual_fix': 'Comisión (M)'
    };

    // El backend guarda "Rendimiento diario (...)" o "Rendimiento (...) del <fecha>" en la descripción de
    // cada pago diario. Acá solo se cambia cómo se muestra; el texto guardado en la base no se toca.
    const formatDescription = (description) => (
        typeof description === 'string' ? description.replace(/^Rendimiento( diario)?(?= \()/, 'Participación del día') : description
    );

    return (
        <>
            {showTabs && (
                <div className="admin-tabs" style={{ marginBottom: '1rem' }}>
                    <button onClick={() => handleTabClick('Todos')} className={activeTab === 'Todos' ? 'active' : ''}>Todos</button>
                    <button onClick={() => handleTabClick('ROI')} className={activeTab === 'ROI' ? 'active' : ''}>Resultados del acuerdo</button>
                    <button onClick={() => handleTabClick('Comisiones')} className={activeTab === 'Comisiones' ? 'active' : ''}>Reconocimientos (comisiones)</button>
                    <button onClick={() => handleTabClick('Deducciones')} className={activeTab === 'Deducciones' ? 'active' : ''}>Ajustes y cargos</button>
                </div>
            )}
            <div className="report-table-container">
                <table className="report-table">
                    <thead><tr><th>Fecha</th><th>Tipo</th><th>Descripción</th><th>ID de Origen</th><th style={{ textAlign: 'right' }}>Monto</th></tr></thead>
                    <tbody>
                        {paginatedTxs.length === 0 ? (
                            <tr><td colSpan="5" style={{ textAlign: 'center' }}>No hay movimientos en esta categoría.</td></tr>
                        ) : (
                            paginatedTxs.map(tx => {
                                const isPositive = tx.amount >= 0;
                                const formattedAmount = `${isPositive ? '+' : ''}${tx.amount.toFixed(2)}`;
                                const amountColor = isPositive ? 'var(--verde-oscuro)' : 'var(--rojo-cerrar)';
                                const formattedType = typeLabels[tx.type] || tx.type.replace(/_/g, ' ');

                                return (
                                    <tr key={tx.id}>
                                        <td data-label="Fecha">{tx.createdAt?.toDate ? tx.createdAt.toDate().toLocaleDateString('es-ES') : 'N/A'}</td>
                                        <td data-label="Tipo"><span className={`status-tag type-${tx.type}`}>{formattedType}</span></td>
                                        <td data-label="Descripción">{formatDescription(tx.description)}</td>
                                        <td data-label="ID de Origen">{tx.sourceTransactionId ? `#${tx.sourceTransactionId.substring(0, 6)}` : 'N/A'}</td>
                                        <td data-label="Monto" style={{ color: amountColor, fontWeight: 'bold', textAlign: 'right' }}>{formattedAmount}</td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
            {totalPages > 1 && (<Pagination totalItems={filteredTransactions.length} itemsPerPage={itemsPerPage} onItemsPerPageChange={setItemsPerPage} currentPage={currentPage} onNext={handleNextPage} onPrevious={handlePreviousPage} canNext={currentPage < totalPages} canPrevious={currentPage > 1} />)}
        </>
    );
};

export default TransactionHistory;